// Fenêtres du planning (lot B14) : les quatre dernières fenêtres historiques de renderModal, reprises une par une
// et posées dans window.MODALES. La cascade de src/legacy/app.js ne les connaît plus.
//
//   exams           les partiels : un registre, un formulaire d'ajout, une suppression annulable
//   sommaire        la consigne et le sommaire à coller dans NotebookLM (texte conservé mot pour mot)
//   fiche_planner   l'aperçu imprimable d'une fiche de séance (corps mis en forme par le serveur)
//   split
//   manage_subparts découper un item en sous-parties : saisie à la main, proposition de l'IA, historique
//
// Les actions historiques (window.actions.addExam, deleteExam, executeSplit, aiSplitSuggest, copyToClipboard…)
// sont APPELÉES, jamais réécrites : ce module ne fait que le rendu et les relais. Les identifiants que ces
// actions relisent dans le DOM sont donc conservés à l'identique — exam-name, exam-date, ai-split-n.
import { html, attr, brut } from '../../core/html.js';
import { state } from '../../core/store.js';
import { actions } from '../../core/actions.js';
import { ico } from '../icones.js';
import { barresMatieres } from '../components/bandeaux.js';

function fermer() { state.modalConfig = null; window.renderModal(); }

// ---------------------------------------------------------------- le chrome commun (.adm-, src/styles/modales.css)
// Le même objet que les fenêtres de Lots et de Podcast : voile, boîte fermée par un filet, tête, corps défilant,
// et un pied qui porte l'action que l'on est venu faire. Sous 768 px, feuille collée en bas.
function fenetre(o) {
    var id = 'fen-' + o.cle + '-titre';
    return html`<div class="adm-voile" data-action="fermerFenetrePlanning" data-voile="1">
        <div class="adm-fen adm-fen--${o.cle} pl-fen" role="dialog" aria-modal="true" aria-labelledby="${id}" data-fenetre="${o.cle}" tabindex="-1">
            <div class="adm-tete">
                <div class="adm-tete-texte">
                    <h2 id="${id}">${o.titre}</h2>
                    ${o.sous ? html`<p class="adm-tete-sous" title="${o.sous}">${o.sous}</p>` : ''}
                </div>
                <button type="button" class="ico-btn" data-action="fermerFenetrePlanning" aria-label="Fermer : ${o.titre}">${ico('x', { taille: 20 })}</button>
            </div>
            <div class="adm-corps">${o.corps}</div>
            ${o.pied ? html`<div class="adm-pied pl-pied">${o.pied}</div>` : ''}
        </div>
    </div>`;
}

// ---------------------------------------------------------------- les partiels
// Un registre : une ligne par partiel, deux niveaux (nom + date + aimant J-N, puis matières et barres), la
// suppression à droite. Puis une section à filet haut pour en ajouter un.
function ligneExam(e, passe) {
    var n = window.daysUntil(e.date);
    var date = window.parseDateLocal(e.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
    var aimant = passe ? ['puce puce-sans-point', 'Passé']
        : (n === 0 ? ['puce puce-danger', "Aujourd'hui"] : (n <= 7 ? ['puce puce-danger', 'J-' + n] : ['puce puce-warn', 'J-' + n]));
    return html`<div class="pl-exam${passe ? ' pl-exam--passe' : ''}">
        <div class="pl-exam-corps">
            <div class="pl-exam-titre">
                <span class="pl-exam-nom">${e.name}</span>
                <span class="pl-exam-date num">${date}</span>
                <span class="${aimant[0]}">${aimant[1]}</span>
            </div>
            ${(e.specs || []).length ? html`<div class="pl-exam-mats">
                ${(e.specs || []).map(function(sp) { return html`<span class="puce puce-sans-point ${window.getSpecialtyStyle(sp)}">${sp}</span>`; })}
            </div>` : ''}
            ${passe ? '' : html`<div class="pl-exam-barres">${barresMatieres(e.specs, false)}</div>`}
        </div>
        <button type="button" class="ico-btn danger" data-action="partielSupprimer" data-args="${attr([e.id])}" aria-label="Supprimer le partiel : ${e.name}" title="Supprimer">${ico('trash-simple', { taille: 18 })}</button>
    </div>`;
}

function corpsExams(c) {
    var d = state.examDraft || { name: '', date: '', specs: [] };
    var aujourd = window.todayStr();
    var avenir = window.activeExams();
    var passes = state.exams.filter(function(e) { return e.date < aujourd; }).sort(function(a, b) { return b.date.localeCompare(a.date); });
    return html`
        <p class="adm-intro">Un partiel fait passer les items de ses matières <b>en tête du programme</b> jusqu'à sa date.</p>
        <p class="adm-note">Chaque séance concernée porte alors l'aimant « Partiel J-N », et le lissage décale les autres items avant elle.</p>

        <section class="pl-sect" aria-label="Partiels à venir">
            ${avenir.length
                ? html`<div class="pl-registre">${avenir.map(function(e) { return ligneExam(e, false); })}</div>`
                : html`<div class="vide pl-vide">
                    ${ico('graduation-cap', { taille: 32 })}
                    <h2>Aucun partiel à venir</h2>
                    <p>Ajoute-en un ci-dessous : ses matières passeront en tête du programme jusqu'au jour dit.</p>
                </div>`}
        </section>

        ${passes.length ? html`<section class="pl-sect" aria-label="Partiels passés">
            <button type="button" class="lien-bouton" data-action="partielsPasses" aria-expanded="${c.passes ? 'true' : 'false'}">${c.passes ? 'Masquer' : 'Afficher'} ${passes.length} partiel${passes.length > 1 ? 's' : ''} passé${passes.length > 1 ? 's' : ''}</button>
            ${c.passes ? html`<div class="pl-registre">${passes.map(function(e) { return ligneExam(e, true); })}</div>` : ''}
        </section>` : ''}

        <section class="pl-sect pl-sect--filet" aria-labelledby="pl-ajout-titre">
            <h3 id="pl-ajout-titre">Ajouter un partiel</h3>
            <div class="pl-ajout-champs">
                <div class="champ"><label for="exam-name">Intitulé (facultatif)</label>
                    <input id="exam-name" type="text" value="${d.name}" placeholder="Ex. Partiel de cardiologie" autocomplete="off"></div>
                <div class="champ"><label for="exam-date">Date</label>
                    <input id="exam-date" type="date" value="${d.date}" min="${aujourd}"></div>
            </div>
            <div class="champ">
                <span class="champ-label" id="pl-mats-titre">Matières au programme${d.specs.length ? ' · ' + d.specs.length + ' choisie' + (d.specs.length > 1 ? 's' : '') : ''}</span>
                <div class="pl-mats" role="group" aria-labelledby="pl-mats-titre">
                    ${window.allSpecialties.map(function(sp, i) {
                        var on = d.specs.indexOf(sp) !== -1;
                        return html`<button type="button" id="pl-mat-${i}" class="puce puce-sans-point pl-mat ${window.getSpecialtyStyle(sp)}" aria-pressed="${on ? 'true' : 'false'}" data-action="partielMatiere" data-args="${attr([sp, i])}">${on ? ico('check', { taille: 14 }) : ''}${sp}</button>`;
                    })}
                </div>
                <span class="aide">Sans matière, le partiel ne change rien à l'ordre du programme.</span>
            </div>
            <button type="button" class="btn btn-primaire btn-pleine" data-action="partielAjouter">${ico('plus', { taille: 16 })}Ajouter ce partiel</button>
        </section>`;
}

// ---------------------------------------------------------------- le sommaire NotebookLM
// Deux textes à copier, mot pour mot. Le compteur de la consigne dit son plafond avant qu'on le dépasse.
function boutonCopier(quoi, libelle, primaire) {
    return html`<button type="button" class="btn ${primaire ? 'btn-primaire' : 'btn-secondaire'} btn-sm pl-copier" data-action="sommaireCopier" data-args="${attr([quoi])}">
        <span class="pl-copier-repos">${ico('copy', { taille: 16 })}${libelle}</span>
        <span class="pl-copier-fait">${ico('check', { taille: 16 })}Copié</span>
    </button>`;
}

function corpsSommaire(c) {
    var n = (c.consigne || '').length, trop = n > 500;
    return html`
        <p class="adm-intro">${c.subtitle || ''}</p>

        <div class="pl-ia">
            <p class="pl-ia-etat">
                ${c.aiApplied
                    ? html`<span class="puce puce-accent">Version IA</span>${c.aiNote ? html` <span class="adm-h">${c.aiNote}</span>` : ''}`
                    : html`<span class="puce puce-sans-point">Version standard</span> <span class="adm-h">issue de la structure de la fiche</span>`}
            </p>
            ${c.aiApplied
                ? html`<button type="button" class="btn btn-fantome btn-sm" data-action="revertSommaireAI">Revenir à la version standard</button>`
                : (window.estAdmin() ? html`<button type="button" class="btn btn-secondaire btn-sm" data-action="improveSommaireWithAI"${c.aiBusy ? html` disabled aria-busy="true"` : ''} title="Consigne optimisée et sommaire enrichi des notions clés EDN, rédigés par l'API Claude">${ico('magic-wand', { taille: 16 })}Améliorer avec l'IA</button>` : '')}
        </div>
        ${c.aiBusy ? html`<p class="adm-note"><span class="puce puce-warn puce-attente">Génération</span> jusqu'à 3 à 4 minutes sur un item complet.</p>` : ''}

        <div class="champ pl-bloc">
            <div class="pl-bloc-tete">
                <label for="sommaire-consigne">1. Consigne — champ « Personnaliser » de NotebookLM</label>
                <span id="sommaire-consigne-count" class="pl-compte num${trop ? ' pl-compte--trop' : ''}">${n} / 500</span>
            </div>
            <textarea id="sommaire-consigne" rows="6" data-champ="modalConfig.consigne" data-live data-input-action="sommaireCompter">${c.consigne || ''}</textarea>
            ${boutonCopier('consigne', 'Copier la consigne', true)}
        </div>

        <div class="champ pl-bloc">
            <label for="sommaire-plan">2. Sommaire détaillé — à ajouter comme source « Texte copié » du notebook</label>
            <textarea id="sommaire-plan" rows="12" class="pl-mono" data-champ="modalConfig.sommaire" data-live>${c.sommaire || ''}</textarea>
            ${boutonCopier('sommaire', 'Copier le sommaire', false)}
        </div>`;
}

// ---------------------------------------------------------------- la fiche de séance imprimable
// Le corps vient du serveur, déjà mis en forme (.fiche-planner, src/styles/legacy.css) : l'écran ne fait que
// l'afficher. Trois états — en chargement, introuvable, lue.
function corpsFiche(c) {
    if (c.erreur) {
        return html`<div class="vide pl-vide">
            ${ico('warning-circle', { taille: 32 })}
            <h2>Fiche introuvable</h2>
            <p>${c.erreur}</p>
        </div>`;
    }
    if (c.html === null) {
        return html`<div class="squelettes" aria-busy="true" aria-label="Chargement de la fiche">
            <div class="squelette squelette-titre"></div>
            <div class="squelette squelette-ligne"></div>
            <div class="squelette squelette-ligne"></div>
            <div class="squelette squelette-bloc"></div>
            <div class="squelette squelette-ligne"></div>
        </div>`;
    }
    if (!c.html) return html`<p class="adm-note">Cette fiche est vide.</p>`;
    return html`<div class="fiche-planner">${brut(c.html)}</div>`;
}

// ---------------------------------------------------------------- scinder / gérer les sous-parties
// Une vraie fenêtre, et pas un formulaire ancré : la saisie est un textarea de plusieurs lignes, et la
// proposition de l'IA est une liste qu'on lit avant de la retenir.
function propositionsMemorisees(c, itemId) {
    var hist = itemId ? window.splitProposalsFor(itemId) : [];
    if (!hist.length) return '';
    return html`<div class="pl-hist">
        <span class="adm-h">Propositions mémorisées (${hist.length})</span>
        ${hist.map(function(h, i) {
            var jour = window.parseDateLocal(window.toYYYYMMDD(new Date(h.t))).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
            var heure = new Date(h.t).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
            return html`<span class="pl-hist-item">
                <button type="button" class="pl-hist-voir" aria-pressed="${c.aiIndex === i ? 'true' : 'false'}" data-action="splitShowProposal" data-args="${attr([i])}" title="Afficher cette proposition"><span class="num">${jour} ${heure}</span> · ${(h.parts || []).length} parties</button>
                <button type="button" class="pl-hist-oubli" data-action="splitForgetProposal" data-args="${attr([i])}" aria-label="Oublier la proposition du ${jour} ${heure}" title="Oublier cette proposition">${ico('x', { taille: 14 })}</button>
            </span>`;
        })}
    </div>`;
}

function corpsSplit(c) {
    if (!c.aiInit) { c.aiInit = true; window.attachSplitHistory(c); }
    var itemId = window.splitModalItemId(c);
    var hist = itemId ? window.splitProposalsFor(itemId) : [];
    var noms = c.aiParts ? c.aiParts.map(function(p) { return p.name; }).join('\n') : '';
    var nonApplique = !!(c.aiParts && (c.inputValue || '').trim() !== noms.trim());
    var n = (c.inputValue || '').split('\n').filter(function(x) { return x.trim(); }).length;
    return html`
        <p class="adm-intro pl-consigne">${c.message}</p>

        <div class="champ pl-bloc">
            <label for="split-parties">Une sous-partie par ligne${n ? ' · ' + n + ' partie' + (n > 1 ? 's' : '') : ''}</label>
            <textarea id="split-parties" rows="${Math.min(12, Math.max(4, n + 1))}" placeholder="Partie 1 : Clinique&#10;Partie 2 : Traitement" data-champ="modalConfig.inputValue" data-live data-input-action="splitCompter">${c.inputValue || ''}</textarea>
            ${c.suggestion ? html`<span class="aide">${c.suggestion}</span>` : ''}
        </div>

        <section class="pl-sect pl-sect--filet" aria-labelledby="pl-ia-titre">
            <h3 id="pl-ia-titre">${ico('magic-wand', { taille: 18 })}Découpage proposé par l'IA</h3>
            <p class="adm-note">D'après la fiche EDN (PDF) et son plan. Les sections rattachées servent au Sommaire NotebookLM et aux flashcards de chaque partie.</p>
            <div class="pl-ia-actions">
                <div class="champ pl-ia-nb"><label for="ai-split-n">Nombre de parties</label>
                    <span class="select-enveloppe"><select id="ai-split-n" class="select">
                        <option value="0">Auto</option><option value="2">2 parties</option><option value="3">3 parties</option>
                        <option value="4">4 parties</option><option value="5">5 parties</option><option value="6">6 parties</option>
                    </select></span></div>
                ${window.estAdmin() ? html`<button type="button" class="btn btn-secondaire btn-sm" data-action="aiSplitSuggest"${c.aiBusy ? html` disabled aria-busy="true"` : ''}>Proposer</button>` : ''}
                <button type="button" class="btn btn-fantome btn-sm" data-action="openLots" data-args="${attr(['split'])}" title="Découper d'un coup tous les items du planning qui ne le sont pas encore : moitié prix, à appliquer item par item">${ico('package', { taille: 16 })}En lot</button>
            </div>
            ${c.aiBusy ? html`<p class="adm-note"><span class="puce puce-warn puce-attente">Analyse de la fiche</span> une à deux minutes.</p>` : ''}
            ${c.aiError ? html`<p class="adm-erreur">${c.aiError}</p>` : ''}
            ${propositionsMemorisees(c, itemId)}
            ${nonApplique ? html`<div class="pl-reprise">
                <span>Proposition mémorisée${hist[c.aiIndex] ? ' du ' + new Date(hist[c.aiIndex].t).toLocaleDateString('fr-FR') : ''}, pas encore placée dans la zone de saisie.</span>
                <button type="button" class="btn btn-secondaire btn-sm" data-action="splitUseProposal" data-args="${attr([c.aiIndex || 0])}">Utiliser cette proposition</button>
            </div>` : ''}
            ${c.aiParts ? html`<ol class="pl-parties">
                ${c.aiParts.map(function(p, i) {
                    return html`<li class="pl-partie">
                        <span class="pl-partie-rang num">${i + 1}</span>
                        <span class="pl-partie-corps">
                            <span class="pl-partie-nom">${p.name}${p.words ? html` <span class="adm-h num">≈ ${p.words} mots</span>` : ''}${p.fusion ? html` <span class="puce puce-warn" title="Deux parties proposées ont été réunies : l'une pesait moins de 100 mots">réunies</span>` : ''}</span>
                            ${p.sections && p.sections.length ? html`<span class="pl-partie-sections">Sections : ${p.sections.join(' · ')}</span>` : ''}
                            ${p.rationale ? html`<span class="pl-partie-raison">${p.rationale}</span>` : ''}
                        </span>
                    </li>`;
                })}
            </ol>
            <p class="adm-note">${nonApplique ? 'Presse « Utiliser cette proposition » pour placer ces noms dans la zone de saisie.' : 'Les noms sont placés dans la zone de saisie : modifie-les si besoin, puis valide.'}${c.aiSource === 'pdf' ? '' : ' Aucun PDF dans edn_data/fiches_edn/ : proposition faite sur le plan seul.'}</p>` : ''}
        </section>`;
}

// ---------------------------------------------------------------- les quatre rendus posés dans window.MODALES
window.MODALES = window.MODALES || {};

window.MODALES.exams = function(c) {
    return String(fenetre({
        cle: 'partiels', titre: 'Mes partiels', sous: 'Ce qui passe en tête du programme du jour',
        corps: corpsExams(c),
        pied: html`<button type="button" class="btn btn-fantome" data-action="fermerFenetrePlanning">Fermer</button>`,
    }));
};

window.MODALES.sommaire = function(c) {
    return String(fenetre({
        cle: 'sommaire', titre: c.title || 'Sommaire NotebookLM',
        corps: corpsSommaire(c),
        pied: html`<button type="button" class="btn btn-fantome" data-action="fermerFenetrePlanning">Fermer</button>`,
    }));
};

window.MODALES.fiche_planner = function(c) {
    var f = c.fiche || {};
    var titre = f.titre || c.titre || ('Item ' + c.item);
    var sous = f.sections
        ? 'Item ' + c.item + ' · ' + f.sections.length + ' section' + (f.sections.length > 1 ? 's' : '') + ' · ' + (f.mots || 0) + ' mots'
        : 'Item ' + c.item;
    // En chargement, la fiche n'a pas encore son titre : sans ce garde-fou la tête écrivait « Item 105 » deux
    // fois, l'une sous l'autre.
    if (sous === titre) sous = '';
    return String(fenetre({
        cle: 'fiche', titre: titre, sous: sous,
        corps: corpsFiche(c),
        pied: html`<p class="adm-note pl-pied-note">Le PDF s'obtient en imprimant la page : « Enregistrer au format PDF ».</p>
            <button type="button" class="btn btn-fantome" data-action="fermerFenetrePlanning">Fermer</button>
            <button type="button" class="btn btn-primaire" data-action="gestionFichePdf" data-args="${attr([c.item])}">${ico('file-pdf', { taille: 16 })}Ouvrir la version imprimable</button>`,
    }));
};

function rendreSplit(c) {
    return String(fenetre({
        cle: 'scinder', titre: c.title || "Scinder l'item",
        corps: corpsSplit(c),
        pied: html`<button type="button" class="btn btn-fantome" data-action="fermerFenetrePlanning">Annuler</button>
            <button type="button" class="btn btn-primaire" data-action="splitValider">Valider le découpage</button>`,
    }));
}
window.MODALES.split = rendreSplit;
window.MODALES.manage_subparts = rendreSplit;

// ---------------------------------------------------------------- les relais
actions.fermerFenetrePlanning = function(el, ev) {
    // Le voile ne ferme que lorsqu'on le vise lui-même, jamais par un clic remonté depuis la boîte.
    if (el && el.hasAttribute && el.hasAttribute('data-voile') && ev && ev.target !== el) return;
    fermer();
};

actions.partielsPasses = function() {
    var c = state.modalConfig; if (!c) return;
    c.passes = !c.passes; window.renderModal();
};

// La saisie en cours est relue par l'action historique (elle lit exam-name / exam-date) : on l'appelle telle
// quelle, puis on rend le focus à l'aimant pressé, que le redessin de la fenêtre venait d'emporter.
actions.partielMatiere = function(sp, i) {
    window.actions.examDraftToggleSpec(sp);
    var el = document.getElementById('pl-mat-' + i);
    if (el) el.focus();
};

// La garde appartient à l'écran : l'action historique répondait par deux alert().
actions.partielAjouter = function() {
    var d = state.examDraft || (state.examDraft = { name: '', date: '', specs: [] });
    var nom = document.getElementById('exam-name'), date = document.getElementById('exam-date');
    if (nom) d.name = nom.value;
    if (date) d.date = date.value;
    if (!d.date) {
        if (window.toast) window.toast('Indique la date du partiel', { type: 'danger', icone: 'warning-circle' });
        if (date) date.focus();
        return;
    }
    if (!d.specs.length) {
        if (window.toast) window.toast('Choisis au moins une matière', { type: 'danger', icone: 'warning-circle' });
        var prem = document.getElementById('pl-mat-0'); if (prem) prem.focus();
        return;
    }
    var nomPartiel = (d.name || '').trim() || ('Partiel ' + d.specs.join(' / '));
    window.actions.addExam();
    if (window.toast) window.toast(nomPartiel + ' ajouté', { icone: 'check' });
};

// On supprime, on annonce, on laisse défaire : le partiel est gardé six secondes dans le toast.
actions.partielSupprimer = function(id) {
    var e = state.exams.find(function(x) { return x.id === id; });
    if (!e) return;
    var copie = JSON.parse(JSON.stringify(e));
    window.actions.deleteExam(id);
    if (window.toast) window.toast('Partiel supprimé : ' + copie.name, { icone: 'trash-simple', action: { label: 'Annuler', fn: function() {
        state.exams.push(copie);
        state.exams.sort(function(a, b) { return a.date.localeCompare(b.date); });
        window.saveExams();
        window.renderModal(); window.renderContent(); window.renderHeader();
    } } });
};

// Le compteur de la consigne se réécrit seul : remplacer la fenêtre entière emporterait le curseur.
actions.sommaireCompter = function(el) {
    var n = (el && el.value ? el.value.length : 0);
    var cible = document.getElementById('sommaire-consigne-count');
    if (!cible) return;
    cible.textContent = n + ' / 500';
    cible.classList.toggle('pl-compte--trop', n > 500);
};

// Le nombre de parties se réécrit dans le libellé du champ, sans redessiner la fenêtre.
actions.splitCompter = function(el) {
    var n = String(el && el.value || '').split('\n').filter(function(x) { return x.trim(); }).length;
    var lab = document.querySelector('label[for="split-parties"]');
    if (lab) lab.textContent = 'Une sous-partie par ligne' + (n ? ' · ' + n + ' partie' + (n > 1 ? 's' : '') : '');
};

actions.splitValider = function() {
    var c = state.modalConfig;
    if (c && typeof c.onConfirm === 'function') c.onConfirm(c.inputValue);
};

// Le bouton dit ce qu'il a fait : « Copié » pendant 1,6 s, et le toast le dit aussi à qui n'a pas les yeux dessus.
actions.sommaireCopier = function(quoi, el) {
    var champ = document.getElementById(quoi === 'consigne' ? 'sommaire-consigne' : 'sommaire-plan');
    if (!champ) return;
    window.actions.copyToClipboard(champ.value, el);
};

// ---------------------------------------------------------------- clavier : Échap ferme, le focus reste dedans
function fenetreOuverte() {
    var c = state.modalConfig;
    if (!c || !window.MODALES[c.type]) return null;
    return document.querySelector('#modal-root [data-fenetre]');
}
function focalisables(fen) {
    return Array.prototype.filter.call(
        fen.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'),
        function(el) { return el.offsetWidth || el.offsetHeight || el.getClientRects().length; });
}
document.addEventListener('keydown', function(ev) {
    if (ev.key !== 'Escape' && ev.key !== 'Tab') return;
    var fen = fenetreOuverte(); if (!fen) return;
    if (ev.key === 'Escape') { ev.preventDefault(); fermer(); return; }
    var liste = focalisables(fen); if (!liste.length) return;
    var premier = liste[0], dernier = liste[liste.length - 1];
    if (!fen.contains(document.activeElement)) { ev.preventDefault(); premier.focus(); return; }
    if (ev.shiftKey && document.activeElement === premier) { ev.preventDefault(); dernier.focus(); }
    else if (!ev.shiftKey && document.activeElement === dernier) { ev.preventDefault(); premier.focus(); }
}, true);

// À l'ouverture, le focus entre dans la fenêtre — sur la fenêtre elle-même, pas sur sa croix : le lecteur d'écran
// lit le titre, et l'anneau cobalt ne vient pas cerner un bouton « Fermer » comme s'il était ce qu'on venait
// faire. Aux redessins suivants le focus est déjà dedans, ou rendu par l'action qui a provoqué le redessin : on
// ne le déplace pas sous les doigts.
var typeOuvert = null;
function apresRendu() {
    var c = state.modalConfig, type = c && window.MODALES[c.type] ? c.type : null;
    if (type === typeOuvert) return;
    typeOuvert = type;
    if (!type) return;
    var fen = fenetreOuverte(); if (!fen) return;
    fen.focus();
}
['exams', 'sommaire', 'fiche_planner', 'split', 'manage_subparts'].forEach(function(t) {
    var rendu = window.MODALES[t];
    window.MODALES[t] = function(c) { var out = rendu(c); requestAnimationFrame(apresRendu); return out; };
});

// Noms historiques : l'inventaire des globales (scripts/inventaire_globals.py) lit le texte des fichiers.
window.actions.fermerFenetrePlanning = actions.fermerFenetrePlanning;
window.actions.partielsPasses = actions.partielsPasses;
window.actions.partielMatiere = actions.partielMatiere;
window.actions.partielAjouter = actions.partielAjouter;
window.actions.partielSupprimer = actions.partielSupprimer;
window.actions.sommaireCompter = actions.sommaireCompter;
window.actions.sommaireCopier = actions.sommaireCopier;
window.actions.splitCompter = actions.splitCompter;
window.actions.splitValider = actions.splitValider;
